(() => {
  'use strict';

  const ZOOM_SCALE = 3.5;
  const MODAL_SELECTOR = '.modal--fullscreen.zoom-gallery';
  const ITEM_SELECTOR = '.zoom-item';
  const IMG_SELECTOR = '.zoom-media img, .zoom-media image';

  function initZoomOnItem(item) {
    if (item._zoomInitialized) return;
    item._zoomInitialized = true;

    const img = item.querySelector(IMG_SELECTOR);
    if (!img) return;

    // Stato
    let zoomed = false;
    let scale = 1;
    let originX = 0, originY = 0;
    let translateX = 0, translateY = 0;

    // Drag
    let isDragging = false;
    let dragStartX = 0, dragStartY = 0;
    let dragStartTranslateX = 0, dragStartTranslateY = 0;

    // Pinch
    let lastPinchDist = null;
    let pinchStartScale = 1;

    img.style.cursor = 'zoom-in';
    img.style.transformOrigin = 'center center';
    img.style.transition = 'transform 0.2s ease';
    img.style.willChange = 'transform';
    img.style.userSelect = 'none';
    img.style.touchAction = 'none';

    function applyTransform(animate = false) {
      img.style.transition = animate ? 'transform 0.2s ease' : 'none';
      img.style.transform = `scale(${scale}) translate(${translateX}px, ${translateY}px)`;
    }

    function clampTranslate() {
      const rect = img.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();

      // Quanto spazio extra ha l'immagine scalata rispetto al container
      const excessW = Math.max(0, (rect.width / scale) * (scale - 1)) / 2;
      const excessH = Math.max(0, (rect.height / scale) * (scale - 1)) / 2;

      translateX = Math.min(excessW / scale, Math.max(-excessW / scale, translateX));
      translateY = Math.min(excessH / scale, Math.max(-excessH / scale, translateY));
    }

    function zoomIn(clientX, clientY) {
      const rect = img.getBoundingClientRect();
      // Calcola il punto di origine relativo all'immagine (0-1)
      originX = (clientX - rect.left) / rect.width;
      originY = (clientY - rect.top) / rect.height;

      // Offset translate per zoomare sul punto cliccato
      translateX = (0.5 - originX) * (rect.width / scale) * (1 - 1 / ZOOM_SCALE) * -1;
      translateY = (0.5 - originY) * (rect.height / scale) * (1 - 1 / ZOOM_SCALE) * -1;

      scale = ZOOM_SCALE;
      zoomed = true;
      img.style.cursor = 'grab';
      applyTransform(true);
    }

    function zoomOut() {
      scale = 1;
      translateX = 0;
      translateY = 0;
      zoomed = false;
      img.style.cursor = 'zoom-in';
      applyTransform(true);
    }

    // ── CLICK (desktop zoom in/out) ──
    img.addEventListener('click', (e) => {
      if (isDragging) return;
      if (!zoomed) {
        zoomIn(e.clientX, e.clientY);
      }
      // zoom out solo col pulsante chiudi
    });

    // ── DRAG (desktop) ──
    img.addEventListener('mousedown', (e) => {
      if (!zoomed) return;
      e.preventDefault();
      isDragging = false;
      dragStartX = e.clientX;
      dragStartY = e.clientY;
      dragStartTranslateX = translateX;
      dragStartTranslateY = translateY;
      img.style.cursor = 'grabbing';
      img.style.transition = 'none';

      const onMove = (e) => {
        const dx = (e.clientX - dragStartX) / scale;
        const dy = (e.clientY - dragStartY) / scale;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) isDragging = true;
        translateX = dragStartTranslateX + dx;
        translateY = dragStartTranslateY + dy;
        clampTranslate();
        applyTransform(false);
      };

      const onUp = () => {
        img.style.cursor = 'grab';
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        // reset isDragging dopo un frame (per non triggerare click)
        requestAnimationFrame(() => { isDragging = false; });
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    });

    // ── TOUCH: drag + pinch (mobile) ──
    img.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        // pinch start
        lastPinchDist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        pinchStartScale = scale;
        e.preventDefault();
      } else if (e.touches.length === 1 && zoomed) {
        // drag start
        dragStartX = e.touches[0].clientX;
        dragStartY = e.touches[0].clientY;
        dragStartTranslateX = translateX;
        dragStartTranslateY = translateY;
        e.preventDefault();
      }
    }, { passive: false });

    img.addEventListener('touchmove', (e) => {
      if (e.touches.length === 2) {
        // pinch zoom
        const dist = Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        );
        scale = Math.min(ZOOM_SCALE * 1.5, Math.max(1, pinchStartScale * (dist / lastPinchDist)));
        zoomed = scale > 1;
        img.style.cursor = zoomed ? 'grab' : 'zoom-in';
        clampTranslate();
        applyTransform(false);
        e.preventDefault();
      } else if (e.touches.length === 1 && zoomed) {
        // drag
        const dx = (e.touches[0].clientX - dragStartX) / scale;
        const dy = (e.touches[0].clientY - dragStartY) / scale;
        translateX = dragStartTranslateX + dx;
        translateY = dragStartTranslateY + dy;
        clampTranslate();
        applyTransform(false);
        e.preventDefault();
      }
    }, { passive: false });

    img.addEventListener('touchend', (e) => {
      if (e.touches.length < 2) lastPinchDist = null;
      // doppio tap per zoom in su mobile (opzionale)
      if (!zoomed && e.changedTouches.length === 1) {
        const now = Date.now();
        if (img._lastTap && now - img._lastTap < 300) {
          zoomIn(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
        }
        img._lastTap = now;
      }
    });
  }

  function resetZoomOnModal(modal) {
    modal.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
      const img = item.querySelector(IMG_SELECTOR);
      if (!img) return;
      img.style.transition = 'transform 0.2s ease';
      img.style.transform = 'scale(1) translate(0px, 0px)';
      img.style.cursor = 'zoom-in';
      // reset stato interno
      item._zoomInitialized = false;
    });
  }

  function initModal(modal) {
    // Init zoom su tutte le slide già presenti
    modal.querySelectorAll(ITEM_SELECTOR).forEach(initZoomOnItem);

    // Reset zoom quando si chiude il modal
    const closeBtn = modal.querySelector('[data-modal-close], [data-close]');
    if (closeBtn && !closeBtn._zoomCloseListened) {
      closeBtn._zoomCloseListened = true;
      closeBtn.addEventListener('click', () => {
        resetZoomOnModal(modal);
      });
    }

    // Reset zoom al cambio slide (scroll sul track)
    const track = modal.querySelector('.gridy-track');
    if (track && !track._zoomScrollListened) {
      track._zoomScrollListened = true;
      track.addEventListener('scroll', () => {
        modal.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
          const img = item.querySelector(IMG_SELECTOR);
          if (!img) return;
          img.style.transition = 'none';
          img.style.transform = 'scale(1) translate(0px, 0px)';
          img.style.cursor = 'zoom-in';
          item._zoomInitialized = false;
        });
        // re-init dopo scroll
        setTimeout(() => {
          modal.querySelectorAll(ITEM_SELECTOR).forEach(initZoomOnItem);
        }, 250);
      });
    }
  }

  function init() {
    document.querySelectorAll(MODAL_SELECTOR).forEach(initModal);

    // Osserva apertura modal (nel caso venga montato dinamicamente)
    const observer = new MutationObserver(() => {
      document.querySelectorAll(MODAL_SELECTOR).forEach((modal) => {
        if (!modal._zoomModalInitialized) {
          modal._zoomModalInitialized = true;
          initModal(modal);
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();